import compact from 'lodash/compact'
import concat from 'lodash/concat'
import countBy from 'lodash/countBy'
import filter from 'lodash/filter'
import find from 'lodash/find'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import join from 'lodash/join'
import map from 'lodash/map'
import reject from 'lodash/reject'
import slice from 'lodash/slice'
import type { FlattenedTree } from '@ztechapps/common.utils'
import type { BlogCategoryRecord } from './types'
import { canMoveBlogCategory } from '@ztechapps/blog.common'
import { flattenTree, rebuildTree } from '@ztechapps/common.utils'

export type BlogCategoryNode = BlogCategoryRecord & { items: BlogCategoryNode[] }
export type FlatBlogCategory = FlattenedTree<BlogCategoryRecord>

const CATEGORY_PATH_SEPARATOR = ' › '

// `rebuildTree` returns a single root, so the top-level blog categories hang under one synthetic node that never renders.
const ROOT_ID = 'category-tree-root'

/** Nests the flat blog category list by each blog category's parent; the returned root is the synthetic `ROOT_ID` node and is never rendered. */
export const buildBlogCategoryTree = (categories: BlogCategoryRecord[]): BlogCategoryNode => {
  const root = { _id: ROOT_ID, name: '', _parentId: null }
  const nodes = map(categories, (category) => ({ ...category, _parentId: category.parent?._id ?? ROOT_ID }))

  return rebuildTree([root, ...nodes] as unknown as FlattenedTree<BlogCategoryNode>[])
}

/** Depth-first rows of every blog category; a top-level blog category has `_depth` 1. */
export const flattenBlogCategoryTree = (tree: BlogCategoryNode): FlatBlogCategory[] =>
  flattenTree(tree, { startsFromRoot: false }) as unknown as FlatBlogCategory[]

/** "Use and Care › Die › DD Round" — the names from the top-level blog category down to `_id`. */
export const buildBlogCategoryPathLabel = (flat: FlatBlogCategory[], _id: string): string =>
  join(
    map(slice(find(flat, { _id })?._path, 1), (pathId) => find(flat, { _id: pathId })?.name),
    CATEGORY_PATH_SEPARATOR
  )

/** Blogs filed directly in each blog category, keyed by blog category `_id` — not the blogs in the blog categories beneath it. */
export const countBlogsByBlogCategory = (blogs: { category?: { _id: string } | null }[]): Record<string, number> =>
  countBy(compact(map(blogs, 'category._id')))

/** Moving a blog to the blog category it is already in does nothing, so that destination is hidden. */
export const getBlogMoveExcludedIds = (blog: { category?: { _id: string } | null }): string[] =>
  compact([blog.category?._id])

/**
 * A blog category cannot move into itself or its own subtree (`canMoveBlogCategory`, the rule the server enforces), and moving
 * it under its current parent does nothing. A row's `_path` starts at the synthetic root and ends at the row itself.
 */
export const getBlogCategoryMoveExcludedIds = (flat: FlatBlogCategory[], _id: string): string[] =>
  compact([
    ...map(
      filter(flat, (row) => !canMoveBlogCategory({ _id }, { _id: row._id, ancestors: slice(row._path, 1, -1) })),
      '_id'
    ),
    find(flat, { _id })?.parent?._id,
  ])

export const TOP_LEVEL_LABEL = 'Top level'
export const NO_MOVE_DESTINATION_LABEL = 'No other category to move to'

/** `categoryId` is the destination's `_id`, or null for the top level (and for the informational item). */
export type MoveToMenuItem = { key: string; label: string; categoryId: string | null; depth: number; disabled: boolean }

/** The items of a Move to… menu: Top level when offered, then every blog category not hidden, in tree order. */
export const buildMoveToMenuModel = (
  flat: FlatBlogCategory[],
  excludedIds: string[],
  showTopLevel: boolean
): MoveToMenuItem[] => {
  const destinations: MoveToMenuItem[] = map(
    reject(flat, (category) => includes(excludedIds, category._id)),
    (category) => ({
      key: category._id,
      label: category.name,
      categoryId: category._id,
      depth: category._depth,
      disabled: false,
    })
  )
  const topLevel: MoveToMenuItem[] = showTopLevel
    ? [{ key: 'top-level', label: TOP_LEVEL_LABEL, categoryId: null, depth: 0, disabled: false }]
    : []
  const items = concat(topLevel, destinations)

  return isEmpty(items)
    ? [{ key: 'none', label: NO_MOVE_DESTINATION_LABEL, categoryId: null, depth: 0, disabled: true }]
    : items
}

/** Delete makes no sense on a reserved blog category or one that still holds a sub-category or a blog. */
export const isBlogCategoryDeleteVisible = ({
  reserved,
  childCount,
  blogCount,
}: {
  reserved: boolean
  childCount: number
  blogCount: number
}): boolean => !reserved && childCount === 0 && blogCount === 0
